import React, { ComponentProps, FC, ReactNode } from 'react';

import TrialBanner from '@adsk/acc-trial-banner';
import { getCurrentToken, getAccOrigin } from '@adsk/acs-sc-web-platform-api';
import {
  EntitlementsProject,
  Account,
  useAlloyFeatureFlag,
} from '@adsk/alloy-provider';
import { Flex } from '@adsk/alloy-react-box';
import { BasicButton } from '@adsk/alloy-react-button';
import Divider from '@adsk/alloy-react-divider';
import Illustration from '@adsk/alloy-react-illustration';
import { WithAlloySuspense } from '@adsk/alloy-react-suspense';
import theme, { StylableComponent } from '@adsk/alloy-react-theme';
import { useSideNavigation } from '@adsk/alloy-side-navigation';
import LegacyTrialBanner from '@adsk/alloy-trial-banner';

import { Product, UserType } from './helpers/types';
import { useTranslation } from './i18n';
import EnvLabel from './layout/EnvLabel';
import Header from './layout/Header';
import ProductButton from './layout/ProductButton';
import ProfileButton from './layout/ProfileButton';
import ProjectButton from './layout/ProjectButton';
import QuestionMarkMenu from './layout/QuestionMarkMenu';
import TopBar from './layout/TopBar';

type HelpLinksType = ComponentProps<typeof QuestionMarkMenu>['helpLinks'];

type TopNavigationProps = StylableComponent<HTMLDivElement> & {
  logo?: boolean;
  login?: boolean;
  admin?: boolean;
  user?: UserType | null;
  showHelpMenu?: boolean;
  projectId?: string;
  accountId?: string;
  currentProductId?: string;
  currentProject?: EntitlementsProject;
  accounts?: Account[];
  products?: Product[];
  onLogout?: () => void;
  helpLinks?: HelpLinksType;
  showTrialBanner?: boolean;
  showSubscriptionsModal?: boolean;
  hideContactSupportFromHelpMenu?: boolean;
  isProductRequired?: boolean;
  renderCustomProductHeader?: () => ReactNode;
  onProductClick?: ({ id, url }: { [key: string]: string }) => void;
  onProjectClick?: (project?: object, url?: string) => void;
  onAccountClick?: (account?: object, url?: string) => void;
  renderCustomRightSection?: () => ReactNode;
  UNSAFE_showBuildersUserProfileModal?: () => void;
  UNSAFE_isBuildersProfileModalEnabled?: boolean;
};

/**
 * Acs Unified TopNavigation
 */
const TopNavigation: FC<TopNavigationProps> = (props) => {
  const {
    style,
    className,
    logo,
    login,
    admin,
    showHelpMenu,
    projectId,
    accountId,
    currentProductId,
    currentProject,
    accounts,
    products,
    user,
    onLogout,
    helpLinks,
    showTrialBanner,
    showSubscriptionsModal,
    hideContactSupportFromHelpMenu,
    isProductRequired,
    renderCustomProductHeader,
    onProductClick,
    onProjectClick,
    onAccountClick,
    renderCustomRightSection,
    UNSAFE_showBuildersUserProfileModal,
    UNSAFE_isBuildersProfileModalEnabled,
  } = props;

  const { t } = useTranslation();
  const { collapsed } = useSideNavigation();

  const { value: enableNewTrialBanner } = useAlloyFeatureFlag(
    'ACSPE_TRIAL_BANNER_MIGRATION',
  );

  return (
    <Flex
      className={className}
      flexDirection="column"
      width="100%"
      style={style}
    >
      <TopBar hideLogo={logo} admin={admin} />
      <EnvLabel />
      <Header
        left={
          <>
            {logo && (
              <a
                data-testid="TopNavigation__Header__Logo__a"
                href={getAccOrigin()}
              >
                <Illustration
                  type={Illustration.TYPES.ACC_TM_FULL_SINGLE_NEW_2022_BLACK}
                  style={{
                    verticalAlign: 'middle',
                    margin: '0 16px',
                  }}
                />
              </a>
            )}
            {'products' in props ? (
              <ProductButton
                productId={currentProductId}
                products={products}
                required={isProductRequired}
                renderCustomHeader={renderCustomProductHeader}
                onProductClick={onProductClick}
              />
            ) : null}

            {!collapsed && 'products' in props && (
              <Divider
                color={collapsed ? theme.colors.white : undefined}
                variant={Divider.VARIANTS.VERTICAL}
                style={{
                  display: 'inline-block',
                  height: 32,
                  margin: '0 16px',
                  [theme.mediaQuery.small]: {
                    display: 'none',
                  },
                }}
              />
            )}

            {'accounts' in props || 'currentProject' in props ? (
              <ProjectButton
                accountId={accountId}
                projectId={projectId}
                productId={currentProductId}
                currentProject={currentProject}
                accounts={accounts}
                onProjectClick={onProjectClick}
                onAccountClick={onAccountClick}
              />
            ) : null}
          </>
        }
        right={
          <>
            {showTrialBanner &&
              (enableNewTrialBanner ? (
                <TrialBanner user={user} currentProductId={currentProductId} />
              ) : (
                <LegacyTrialBanner
                  user={user}
                  currentProductId={currentProductId}
                />
              ))}

            {renderCustomRightSection?.()}

            {showHelpMenu && (
              <QuestionMarkMenu
                helpLinks={helpLinks}
                currentProductId={currentProductId}
                isProductRequired={isProductRequired}
                hideContactSupport={hideContactSupportFromHelpMenu}
              />
            )}

            {(showHelpMenu || showTrialBanner) && (
              <Divider
                variant={Divider.VARIANTS.VERTICAL}
                style={{
                  height: 20,
                  margin: '0px 16px',
                }}
              />
            )}

            {'user' in props && (
              <ProfileButton
                user={user}
                projectId={projectId}
                onLogout={onLogout}
                showSubscriptionsModal={showSubscriptionsModal}
                UNSAFE_showBuildersUserProfileModal={
                  UNSAFE_showBuildersUserProfileModal
                }
                UNSAFE_isBuildersProfileModalEnabled={
                  UNSAFE_isBuildersProfileModalEnabled
                }
              />
            )}

            {login && !('user' in props) && (
              <BasicButton
                style={theme.typography.heading4}
                onClick={() => getCurrentToken()}
              >
                {t('SIGN_IN')}
              </BasicButton>
            )}
          </>
        }
      />
    </Flex>
  );
};

TopNavigation.displayName = 'TopNavigation';

export default WithAlloySuspense(TopNavigation);
